import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/ApiConfig';
import DefaultModal from '../modals/DefaultModal';
import useDefaultModal from '../hooks/useDefaultModal';
import useProgramaSituacao from '../hooks/useProgramaSituacao';
import CNPJMaskInput from '../masks/CNPJMaskInput';
import { formatCNPJ, unformatCNPJ } from '../../utils/cnpjUtils';
import CPFMaskInput from '../masks/CPFMaskInput';
import { formatCPF, unformatCPF } from '../../utils/cpfUtils';
import ModalFavorecidoFind from './ModalFavorecidoFind';
import useCheckControlledInputs from '../hooks/useCheckControlledInputs';
import {
  formatCurrencyView,
  handleCurrencyChange,
  unformatCurrency,
} from '../../utils/currencyUtils';

const CHAR_LIMITS = {
  objetoDetalhado: 500,
  objetoResumido: 100,
};

const DespesaModal = ({ show, onClose, despesa, onSaveSuccess }) => {
  useCheckControlledInputs();
  const { isProgramaAtivo } = useProgramaSituacao();
  const [isProgramaInativo, setIsProgramaInativo] = useState(false);
  const [formData, setFormData] = useState({
    ano: '',
    idDespesa: '',
    idPrograma: '',
    idAcao: '',
    idSubAcao: '',
    idItem: '',
    cnpjAssociacao: '',
    idElementoDespesa: '',
    idFonteRecurso: '',
    idFavorecido: '',
    nomeFavorecido: '',
    objetoResumido: '',
    objetoDetalhado: '',
    notaFiscal: '',
    dataNf: '',
    dataElaboracao: '',
    dataPagamento: '',
    formaPagamento: '',
    valor: '',
    numConta: '',
  });

  const [programas, setProgramas] = useState([]);
  const [acoes, setAcoes] = useState([]);
  const [subAcoes, setSubAcoes] = useState([]);
  const [itens, setItens] = useState([]);
  const [elementosDespesa, setElementosDespesa] = useState([]);
  const [fonteRecursos, setFonteRecursos] = useState([]);
  const [contas, setContas] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [elementoAutoSelecionado, setElementoAutoSelecionado] = useState(false);
  const objetoDetalhadoRef = useRef(null);

  const token = localStorage.getItem('token');
  const cnpj = localStorage.getItem('cnpj');
  const anoBase = localStorage.getItem('anoBase');
  const { setDefaultModal, showDefaultModal } = useDefaultModal();
  const [isModalFavorecidoSelect, setIsModalFavorecidoSelect] = useState(false);
  const [charCounts, setCharCounts] = useState({
    objetoDetalhado: 0,
    objetoResumido: 0,
  });

  const isEdit = !!despesa?.idDespesa;
  const isContaDisabled = contas.length === 1;

  useEffect(() => {
    if (show) {
      const fetchInitialData = async () => {
        setIsLoading(true);
        try {
          const programaIdParaAcoes =
            despesa?.idPrograma || despesa?.programa?.id;

          const requests = [
            axios.get(`${API_BASE_URL.PROGRAMA}/all`, {
              params: { cnpjAssociacao: cnpj },
              headers: { Authorization: `Bearer ${token}` },
            }),
            axios.get(`${API_BASE_URL.ELEMENTO_DESPESA}/all`, {
              headers: { Authorization: `Bearer ${token}` },
            }),
            axios.get(`${API_BASE_URL.FONTE_RECURSO}/all`, {
              params: { cnpjAssociacao: cnpj },
              headers: { Authorization: `Bearer ${token}` },
            }),
          ];

          if (programaIdParaAcoes) {
            requests.push(
              axios.get(`${API_BASE_URL.ACAO}/all`, {
                params: {
                  cnpjAssociacao: cnpj,
                  idPrograma: programaIdParaAcoes,
                },
                headers: { Authorization: `Bearer ${token}` },
              })
            );
          }

          const responses = await Promise.all(requests);

          setProgramas(responses[0].data);
          setElementosDespesa(responses[1].data);
          setFonteRecursos(responses[2].data);

          if (responses.length > 3) {
            setAcoes(responses[3].data);
          }

          if (despesa) {
            let favorecidoData = despesa.favorecido || {};
            let cpfOrCnpjValue = '';

            if (favorecidoData.cpfOrCnpj) {
              cpfOrCnpjValue = favorecidoData.cpfOrCnpj;
            } else if (despesa.idFavorecido) {
              cpfOrCnpjValue = despesa.idFavorecido;
            }

            // Carregar contas para o programa da despesa sendo editada
            const programaIdParaContas = despesa.idPrograma || despesa.programa?.id;
            if (programaIdParaContas) {
              try {
                const contasRes = await axios.get(`${API_BASE_URL.CONTA}/all`, {
                  params: {
                    idPrograma: programaIdParaContas,
                    cnpjAssociacao: cnpj,
                  },
                  headers: { Authorization: `Bearer ${token}` },
                });
                setContas(contasRes.data || []);
              } catch (err) {
                console.error('Erro ao carregar contas para edição:', err);
                setContas([]);
              }
            }

            const newFormData = {
              ano: despesa.ano || '',
              idDespesa: despesa.idDespesa || '',
              idPrograma: despesa.idPrograma || despesa.programa?.id || '',
              idAcao: despesa.idAcao || despesa.acao?.id || '',
              idSubAcao: despesa.idSubAcao || '',
              idItem: despesa.idItem || '',
              cnpjAssociacao: despesa.cnpjAssociacao || cnpj || '',
              idElementoDespesa:
                despesa.idElementoDespesa || despesa.elementoDespesa?.id || '',
              idFonteRecurso:
                despesa.idFonteRecurso || despesa.fonteRecurso?.id || '',
              idFavorecido: cpfOrCnpjValue,
              nomeFavorecido: despesa.nomeFavorecido,
              objetoResumido: despesa.objetoResumido || '',
              objetoDetalhado: despesa.objetoDetalhado || '',
              notaFiscal: despesa.notaFiscal || '',
              dataNf: despesa.dataNf || '',
              dataElaboracao: despesa.dataElaboracao || '',
              dataPagamento: despesa.dataPagamento || '',
              formaPagamento: despesa.formaPagamento || '',
              valor: formatCurrencyView(despesa.valor ?? 0),
              numConta: despesa.numConta || despesa.conta?.numConta || '',
            };

            setFormData(newFormData);
            setCharCounts({
              objetoDetalhado: (newFormData.objetoDetalhado || '').length,
              objetoResumido: (newFormData.objetoResumido || '').length,
            });
          } else {
            setFormData({
              ano: '',
              idDespesa: '',
              idPrograma: '',
              idAcao: '',
              idSubAcao: null,
              idItem: null,
              cnpjAssociacao: cnpj || '',
              idElementoDespesa: '',
              idFonteRecurso: '',
              idFavorecido: '',
              nomeFavorecido: '',
              objetoResumido: '',
              objetoDetalhado: '',
              notaFiscal: '',
              dataNf: '',
              dataElaboracao: '',
              dataPagamento: '',
              formaPagamento: '',
              valor: '',
              numConta: '',
            });

            setCharCounts({
              objetoDetalhado: 0,
              objetoResumido: 0,
            });
            setElementoAutoSelecionado(false);
          }
        } catch (error) {
          console.error('Erro ao carregar dados:', error);
        } finally {
          setIsLoading(false);
        }
      };

      fetchInitialData();
    }
  }, [show, despesa, cnpj, token]);

  // Verificar se o programa está encerrado quando idPrograma mudar
  useEffect(() => {
    if (formData.idPrograma) {
      const ativo = isProgramaAtivo(formData.idPrograma);
      setIsProgramaInativo(!ativo);
    } else {
      setIsProgramaInativo(false);
    }
  }, [formData.idPrograma, isProgramaAtivo]);

  useEffect(() => {
    if (!formData.idPrograma) {
      setAcoes([]);
      setFormData(prev => ({ ...prev, idAcao: '' }));
      return;
    }

    axios
      .get(`${API_BASE_URL.ACAO}/all`, {
        params: { cnpjAssociacao: cnpj, idPrograma: formData.idPrograma },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(res => {
        const novasAcoes = res.data;
        setAcoes(novasAcoes);

        const acaoValida = novasAcoes.some(acao => acao.id === formData.idAcao);

        if (!acaoValida) {
          setFormData(prev => ({ ...prev, idAcao: '' }));
        }
      })
      .catch(err => {
        console.error('Erro ao carregar ações:', err);
        setAcoes([]);
        setFormData(prev => ({ ...prev, idAcao: '' }));
      });
  }, [formData.idPrograma, cnpj, token, isEdit]);

  useEffect(() => {
    if (!formData.idPrograma) {
      console.log('Resetando contas - programa vazio');
      setContas([]);
      setFormData(prev => ({ ...prev, numConta: '' }));
      return;
    }

    // Carregar contas do programa para a associação
    console.log('Carregando contas para programa:', formData.idPrograma, 'CNPJ:', cnpj);
    setContas([]); // Resetar contas imediatamente ao trocar de programa
    axios
      .get(`${API_BASE_URL.CONTA}/all`, {
        params: { 
          idPrograma: formData.idPrograma,
          cnpjAssociacao: cnpj
        },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(res => {
        const contasCarregadas = res.data;
        console.log('Contas carregadas:', contasCarregadas);
        if (!Array.isArray(contasCarregadas)) {
          console.error('Resposta não é um array:', contasCarregadas);
          setContas([]);
          return;
        }
        setContas(contasCarregadas);
        // Se houver apenas uma conta, selecionar automaticamente
        if (contasCarregadas.length === 1 && !formData.numConta) {
          setFormData(prev => ({ ...prev, numConta: contasCarregadas[0].numero }));
        }
      })
      .catch(err => {
        console.error('Erro ao carregar contas:', err.response?.data || err.message);
        setContas([]);
      });
  }, [formData.idPrograma, cnpj, token]);

  useEffect(() => {
    if (!formData.idPrograma || !formData.idAcao) {
      setSubAcoes([]);
      setFormData(prev => ({ ...prev, idSubAcao: null, idItem: null }));
      return;
    }

    axios
      .get(`${API_BASE_URL.SUB_ACAO}/all`, {
        params: {
          cnpjAssociacao: cnpj,
          idPrograma: formData.idPrograma,
          idAcao: formData.idAcao,
        },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(res => {
        setSubAcoes(res.data);
      })
      .catch(err => {
        console.error('Erro ao carregar sub-ações:', err);
        setSubAcoes([]);
      });
  }, [formData.idPrograma, formData.idAcao, cnpj, token]);

  useEffect(() => {
    if (!formData.idPrograma || !formData.idAcao) {
      setItens([]);
      setFormData(prev => ({ ...prev, idItem: null }));
      return;
    }

    let endpoint;
    let params = {
      cnpjAssociacao: cnpj,
      idPrograma: formData.idPrograma,
      idAcao: formData.idAcao,
    };

    if (
      formData.idSubAcao &&
      formData.idSubAcao !== '' &&
      formData.idSubAcao !== null
    ) {
      endpoint = `${API_BASE_URL.ITEM}/subacao`;
      params.idSubAcao = formData.idSubAcao;
    } else {
      endpoint = `${API_BASE_URL.ITEM}/acao`;
    }

    axios
      .get(endpoint, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(res => {
        setItens(res.data);
      })
      .catch(err => {
        console.error('Erro ao carregar itens:', err);
        setItens([]);
      });
  }, [formData.idPrograma, formData.idAcao, formData.idSubAcao, cnpj, token]);

  const handleFavorecidoSelect = favorecido => {
    setFormData(prev => ({
      ...prev,
      idFavorecido: favorecido.cpfOrCnpj,
      nomeFavorecido: favorecido.nome || '',
      favorecido: favorecido,
    }));
  };

  const handleValorChange = e => {
    handleCurrencyChange(e, setFormData);
  };

  const handleChange = e => {
    const { name, value } = e.target;
    let processedValue = value;

    if (
      [
        'idPrograma',
        'idAcao',
        'idSubAcao',
        'idItem',
        'idElementoDespesa',
        'idFonteRecurso',
      ].includes(name)
    ) {
      if (['idSubAcao', 'idItem'].includes(name)) {
        processedValue = value ? parseInt(value) : null;
      } else {
        processedValue = value ? parseInt(value) : '';
      }
    }

    if (typeof processedValue === 'string') {
      processedValue = processedValue.toUpperCase();
    }

    setFormData(prev => {
      const newFormData = {
        ...prev,
        [name]: processedValue,
      };

      if (name === 'idPrograma') {
        newFormData.idAcao = '';
        newFormData.idSubAcao = null;
        newFormData.idItem = null;
        newFormData.numConta = '';
        setElementoAutoSelecionado(false);
      } else if (name === 'idAcao') {
        newFormData.idSubAcao = null;
        newFormData.idItem = null;
        setElementoAutoSelecionado(false);
      } else if (name === 'idSubAcao') {
        newFormData.idItem = null;
        setElementoAutoSelecionado(false);
      } else if (name === 'idItem') {
        if (value && itens.length > 0) {
          const itemSelecionado = itens.find(
            item => item.id === parseInt(value)
          );
          if (itemSelecionado && itemSelecionado.idElementoDespesa) {
            newFormData.idElementoDespesa = itemSelecionado.idElementoDespesa;
            setElementoAutoSelecionado(true);
          } else {
            setElementoAutoSelecionado(false);
          }
        } else {
          setElementoAutoSelecionado(false);
        }
      } else if (name === 'idElementoDespesa') {
        setElementoAutoSelecionado(false);
      }

      return newFormData;
    });
  };

  const handleSubmit = e => {
    e.preventDefault();

    if (!formData.idPrograma) {
      showDefaultModal('error', 'Por favor, selecione um programa.');
      return;
    }

    // Validar se o programa está vigente
    if (formData.idPrograma && !isProgramaAtivo(formData.idPrograma)) {
      showDefaultModal('error', 'Este programa está ENCERRADO. Não é possível salvar despesas para programas encerrados.');
      return;
    }

    if (!formData.numConta) {
      showDefaultModal('error', 'Por favor, selecione uma conta.');
      return;
    }

    if (!formData.idFavorecido) {
      showDefaultModal('error', 'Por favor, selecione um favorecido.');
      return;
    }

    if (!formData.idElementoDespesa) {
      showDefaultModal('error', 'Por favor, selecione um elemento de despesa.');
      return;
    }

    if (!formData.idFonteRecurso) {
      showDefaultModal('error', 'Por favor, selecione uma fonte de recurso.');
      return;
    }

    if (!formData.dataElaboracao) {
      showDefaultModal('error', 'Por favor, informe a data de elaboração.');
      return;
    }

    const payload = {
      ...formData,
      ano: formData.ano || anoBase,
      cnpjAssociacao: cnpj,
      idPrograma: formData.idPrograma || null,
      idAcao: formData.idAcao || null,
      idSubAcao: formData.idSubAcao || null,
      idItem: formData.idItem || null,
      valor: unformatCurrency(formData.valor),
    };

    const url = isEdit
      ? `${API_BASE_URL.DESPESA}/update`
      : `${API_BASE_URL.DESPESA}/save`;

    const method = isEdit ? 'put' : 'post';

    axios({
      method,
      url,
      data: payload,
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(response => {
        let savedData = response.data;

        if (typeof response.data !== 'object') {
          savedData = {
            ...payload,
            idDespesa: response.data,
          };
        }

        showDefaultModal('success', 'Registro salvo com sucesso!', () => {
          onSaveSuccess(savedData);
          onClose();
        });
      })
      .catch(error => {
        console.error('Erro ao salvar formData:', error);
        const errorMessage =
          error.response?.data?.message || 'Erro ao salvar. Tente novamente.';
        showDefaultModal('error', errorMessage);
      });
  };

  function handleDescricaoInput(event) {
    const { name, value, selectionStart } = event.target;
    const newValue = value.slice(0, CHAR_LIMITS[name]);
    setFormData(prevState => ({
      ...prevState,
      [name]: newValue,
    }));

    setCharCounts(prevCounts => ({
      ...prevCounts,
      [name]: newValue.length,
    }));

    setTimeout(() => {
      if (name === 'objetoDetalhado') {
        objetoDetalhadoRef.current.selectionStart = selectionStart;
        objetoDetalhadoRef.current.selectionEnd = selectionStart;
      }
    }, 0);
  }

  if (!show) return null;

  return (
    <>
      <div className="modal-backdrop fade show"></div>
      <div
        className="modal fade show d-block"
        tabIndex="-1"
        role="dialog"
        style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      >
        <div
          className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable"
          role="document"
        >
          <div className="modal-content">
            <div
              className="modal-header bg-success text-white"
              style={{ height: '30px' }}
            >
              <h5 className="modal-title">
                {isEdit ? 'EDITAR DESPESA' : 'NOVA DESPESA'}
              </h5>
              <button
                type="button"
                className="btn-close btn-close-white"
                onClick={onClose}
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              {isProgramaInativo && (
                <div className="alert alert-warning alert-dismissible fade show mb-3" role="alert">
                  <i className="bi bi-exclamation-triangle-fill me-2"></i>
                  <strong>Atenção:</strong> Este programa está <strong>ENCERRADO</strong>. Você pode visualizar os dados, mas não é possível salvar alterações.
                </div>
              )}
              {isLoading ? (
                <div className="text-center py-4">Carregando dados...</div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <div className="row g-2 small">
                    <div className="col-12 col-sm-6 col-md-4 input-group-sm">
                      <label className="form-label mb-1">
                        Programa{' '}
                        <small className="text-muted">(opcional)</small>
                      </label>
                      <select
                        name="idPrograma"
                        className="form-select"
                        value={formData.idPrograma}
                        onChange={handleChange}
                      >
                        <option value="">Selecione...</option>
                        {programas
                          .filter(programa => programa && programa.id)
                          .map(programa => (
                            <option key={programa.id} value={programa.id}>
                              {programa.descricao}
                            </option>
                          ))}
                      </select>
                    </div>

                    <div className="col-12 col-sm-6 col-md-4 input-group-sm">
                      <label className="form-label mb-1">
                        Ação <small className="text-muted">(opcional)</small>
                      </label>
                      <select
                        name="idAcao"
                        className="form-select"
                        value={formData.idAcao}
                        onChange={handleChange}
                        disabled={!formData.idPrograma}
                      >
                        <option value="">Selecione...</option>
                        {acoes
                          .filter(acao => acao && acao.id)
                          .map(acao => (
                            <option key={acao.id} value={acao.id}>
                              {acao.descricao}
                            </option>
                          ))}
                      </select>
                    </div>

                    <div className="col-12 col-sm-6 col-md-4 input-group-sm">
                      <label className="form-label mb-1">Sub-Ação</label>
                      <select
                        name="idSubAcao"
                        className="form-select"
                        value={formData.idSubAcao || ''}
                        onChange={handleChange}
                        disabled={!formData.idAcao || subAcoes.length === 0}
                        title="Campo opcional - deixe em branco se não houver sub-ação"
                      >
                        <option value="">Selecione...</option>
                        {subAcoes
                          .filter(subAcao => subAcao && subAcao.id)
                          .map(subAcao => (
                            <option key={subAcao.id} value={subAcao.id}>
                              {subAcao.descricao}
                            </option>
                          ))}
                      </select>
                    </div>

                    <div className="col-12 col-sm-6 col-md-4 input-group-sm">
                      <label className="form-label mb-1">Item</label>
                      <select
                        name="idItem"
                        className="form-select"
                        value={formData.idItem || ''}
                        onChange={handleChange}
                        disabled={!formData.idAcao || itens.length === 0}
                        title="Campo opcional - deixe em branco se não houver item específico"
                      >
                        <option value="">Selecione...</option>
                        {itens
                          .filter(item => item && item.id)
                          .map(item => (
                            <option key={item.id} value={item.id}>
                              {item.descricao}
                            </option>
                          ))}
                      </select>
                    </div>

                    <div className="col-12 col-sm-6 col-md-4 input-group-sm">
                      <label className="form-label mb-1">
                        Elemento da Despesa *
                        {elementoAutoSelecionado && (
                          <small className="text-success ms-2">
                            <i className="bi bi-check-circle-fill me-1"></i>
                            (selecionado automaticamente)
                          </small>
                        )}
                      </label>
                      <select
                        name="idElementoDespesa"
                        className={`form-select ${
                          elementoAutoSelecionado ? 'border-success' : ''
                        }`}
                        value={formData.idElementoDespesa}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Selecione...</option>
                        {elementosDespesa
                          .filter(elemento => elemento && elemento.id)
                          .map(elemento => (
                            <option key={elemento.id} value={elemento.id}>
                              {elemento.descricao}
                            </option>
                          ))}
                      </select>
                    </div>

                    <div className="col-12 col-sm-6 col-md-4 input-group-sm">
                      <label className="form-label mb-1">
                        Fonte de Recursos *
                      </label>
                      <select
                        name="idFonteRecurso"
                        className="form-select"
                        value={formData.idFonteRecurso}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Selecione...</option>
                        {fonteRecursos
                          .filter(fonte => fonte && fonte.id)
                          .map(fonte => (
                            <option key={fonte.id} value={fonte.id}>
                              {fonte.descricao}
                            </option>
                          ))}
                      </select>
                    </div>

                    <div className="col-12 col-sm-6 col-md-3 input-group-sm">
                      <label className="form-label mb-1">
                        {(formData.idFavorecido || '').length === 11
                          ? 'CPF do Favorecido *'
                          : 'CNPJ do Favorecido *'}
                      </label>
                      <div className="input-group input-group-sm">
                        {(formData.idFavorecido || '').length === 11 ? (
                          <CPFMaskInput
                            value={formatCPF(formData.idFavorecido || '')}
                            onChange={e =>
                              handleChange({
                                target: {
                                  name: 'idFavorecido',
                                  value: unformatCPF(e.target.value),
                                },
                              })
                            }
                            name="idFavorecido"
                            type="text"
                            disabled
                            readOnly
                          />
                        ) : (
                          <CNPJMaskInput
                            value={formatCNPJ(formData.idFavorecido || '')}
                            onChange={e =>
                              handleChange({
                                target: {
                                  name: 'idFavorecido',
                                  value: unformatCNPJ(e.target.value),
                                },
                              })
                            }
                            name="idFavorecido"
                            type="text"
                            disabled
                            readOnly
                          />
                        )}
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => setIsModalFavorecidoSelect(true)}
                        >
                          <i className="bi bi-three-dots" />
                        </button>
                      </div>
                    </div>

                    <div className="col-12 col-sm-6 col-md-6 input-group-sm">
                      <label className="form-label mb-1">
                        Nome do Favorecido
                      </label>
                      <input
                        disabled
                        className="form-control"
                        name="nomeFavorecido"
                        value={formData.nomeFavorecido || ''}
                        readOnly
                      />
                    </div>

                    <div className="col-md-3 input-group-sm">
                      <label className="form-label mb-1">
                        Data Elaboração *
                      </label>
                      <input
                        type="date"
                        className="form-control"
                        name="dataElaboracao"
                        value={formData.dataElaboracao}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    <div className="col-md-3 input-group-sm">
                      <label className="form-label mb-1">Nota Fiscal</label>
                      <input
                        type="text"
                        className="form-control"
                        name="notaFiscal"
                        value={formData.notaFiscal}
                        onChange={handleChange}
                      />
                    </div>

                    <div className="col-md-3 input-group-sm">
                      <label className="form-label mb-1">Data da Nota</label>
                      <input
                        type="date"
                        className="form-control"
                        name="dataNf"
                        value={formData.dataNf}
                        onChange={handleChange}
                      />
                    </div>

                    <div className="col-md-3 input-group-sm">
                      <label className="form-label mb-1">Valor *</label>
                      <input
                        type="text"
                        className="form-control text-end"
                        value={formData.valor || 'R$ 0,00'}
                        onChange={handleValorChange}
                        onFocus={e => e.target.select()}
                        required
                      />
                    </div>

                    <div className="col-md-3 input-group-sm">
                      <label className="form-label mb-1">Nº Conta</label>
                      <select
                        className="form-control"
                        name="numConta"
                        value={formData.numConta || ''}
                        onChange={handleChange}
                        disabled={isContaDisabled || !formData.idPrograma}
                      >
                        <option value="">Selecione uma conta</option>
                        {contas.map(conta => (
                          <option key={conta.numero} value={conta.numero}>
                            {conta.numero}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-md-6 input-group-sm">
                      <label className="form-label mb-1">
                        Objeto resumido (
                        {formData.objetoResumido
                          ? formData.objetoResumido.length
                          : 0}
                        /{CHAR_LIMITS.objetoResumido})
                      </label>
                      <input
                        className="form-control"
                        name="objetoResumido"
                        onChange={handleDescricaoInput}
                        value={formData.objetoResumido}
                      />
                    </div>

                    <div className="col-md-12 input-group-sm">
                      <label className="form-label mb-1">
                        Objeto detalhado ({charCounts.objetoDetalhado}/
                        {CHAR_LIMITS.objetoDetalhado})
                      </label>
                      <textarea
                        className="form-control"
                        rows="1"
                        ref={objetoDetalhadoRef}
                        onInput={handleDescricaoInput}
                        value={formData.objetoDetalhado}
                        name="objetoDetalhado"
                        style={{ resize: 'vertical' }}
                      />
                    </div>
                  </div>

                  <div className="modal-footer mt-2 pt-2">
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={onClose}
                    >
                      Cancelar
                    </button>
                    <button 
                      type="submit" 
                      className="btn btn-success btn-sm"
                      disabled={isProgramaInativo}
                      title={isProgramaInativo ? 'Programa ENCERRADO - não é possível salvar' : (isEdit ? 'Atualizar despesa' : 'Salvar despesa')}
                    >
                      {isEdit ? 'Atualizar' : 'Salvar'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>

      <ModalFavorecidoFind
        show={isModalFavorecidoSelect}
        onClose={() => setIsModalFavorecidoSelect(false)}
        onItemSelect={handleFavorecidoSelect}
      />

      <DefaultModal {...setDefaultModal} />
    </>
  );
};

export default DespesaModal;
